import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layers, FileJson, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { download } from "@/lib/imports";
import { useAuth } from "@/context/AuthContext";
import { CardGrid, CardGridSkeleton } from "@/components/cards/CardGrid";
import { EmptyState, PageHeader } from "@/components/common/PageParts";
import { PageLoader } from "@/components/common/PageLoader";
import { VisibilityBadge } from "@/components/common/Badges";

export default function SetDetail() {
  const { setId } = useParams();
  const { user } = useAuth();
  const { data: set, isLoading, isError } = useQuery({ queryKey: ["sets", "detail", setId], queryFn: () => api.get(`/sets/${setId}`).then((r) => r.data), retry: false });
  const mine = user && set && user.user_id === set.owner_id;
  const { data: cards, isLoading: loadingCards } = useQuery({
    queryKey: ["cards", "set", setId, Boolean(mine)],
    queryFn: () => api.get(mine ? "/me/cards" : "/cards", { params: { set_id: setId, sort: "name", page_size: 100 } }).then((r) => r.data),
    enabled: Boolean(set),
  });

  const exportAs = (kind) => download(`/export/${kind === "zip" ? "package.zip" : "cards.json"}`, { set_id: setId }, `${set.code}-planar-foundry.${kind}`.toLowerCase())
    .catch((err) => toast.error(formatApiError(err)));

  if (isLoading) return <PageLoader />;
  if (isError) return <div className="container-x py-24"><EmptyState title="Set not found" testid="set-not-found" /></div>;

  return (
    <div className="container-x py-12" data-testid="set-detail-page">
      <PageHeader eyebrow={`Set · ${set.code}`} title={set.name} description={set.description} testid="set-detail-header"
        actions={
          <div className="flex items-center gap-3 text-sm text-slate-400">
            <VisibilityBadge visibility={set.visibility} />
            {set.owner && <Link to={`/u/${set.owner.username}`} className="hover:text-amber-200" data-testid="set-owner-link">by @{set.owner.username}</Link>}
            {mine && <button className="btn-ghost px-2 py-1 text-xs" onClick={() => exportAs("json")} data-testid="set-export-json"><FileJson className="h-3.5 w-3.5" />Export JSON</button>}
            {mine && <button className="btn-ghost px-2 py-1 text-xs" onClick={() => exportAs("zip")} data-testid="set-export-zip"><Package className="h-3.5 w-3.5" />Export package</button>}
          </div>
        } />
      {(set.creator_credit || set.release_date || set.set_version || set.mechanics_glossary?.length > 0) && (
        <div className="panel mb-8 space-y-2 p-5 text-sm text-slate-300" data-testid="set-details">
          <p className="font-mono text-xs text-slate-500">{[set.creator_credit && `Created by ${set.creator_credit}`, set.release_date && `Released ${set.release_date}`, set.set_version && `Version ${set.set_version}`].filter(Boolean).join(" · ")}</p>
          {set.mechanics_glossary?.map((m) => <p key={m.name}><strong className="text-amber-200">{m.name}</strong> <span className="text-slate-400">{m.reminder_text}</span></p>)}
        </div>
      )}
      {loadingCards ? <CardGridSkeleton /> : cards?.items?.length ? (
        <CardGrid cards={cards.items} showOwner={false} testid="set-cards-grid" />
      ) : (
        <EmptyState icon={Layers} title="No cards in this set yet" testid="set-cards-empty">
          {mine && <Link to="/upload/bulk" className="text-amber-300 hover:underline" data-testid="set-bulk-upload-link">Bulk upload cards with set code {set.code}</Link>}
        </EmptyState>
      )}
    </div>
  );
}
